import { z } from 'zod';

import { ReceptionBelongingsSnapshotSchema } from './reception-belongings.schemas.js';
import { ReceptionChecklistItemSchema } from './reception-capture.schemas.js';
import { ReceptionDamageSchema } from './reception-damage.schemas.js';
import { ReceptionTermSnapshotSchema } from './reception-term.schemas.js';

export const RECEPTION_STATUSES = ['OPEN', 'COMPLETED', 'CANCELLED'] as const;

export const ListReceptionsQuerySchema = z.object({
  status: z.enum(RECEPTION_STATUSES).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export type ListReceptionsQuery = z.infer<typeof ListReceptionsQuerySchema>;

export const ReceptionListItemSchema = z.object({
  id: z.uuid(),
  placeId: z.uuid(),
  partyId: z.uuid(),
  customerRoleId: z.uuid(),
  targetType: z.enum(['VEHICLE', 'PART', 'CONTAINER']),
  customerVehicleId: z.uuid().nullable(),
  containerLabel: z.string().nullable(),
  partyDisplayName: z.string(),
  vehiclePlate: z.string().nullable(),
  vehiclePlateNormalized: z.string().nullable(),
  receptionMode: z.enum(['FULL', 'EXPRESS']),
  status: z.enum(RECEPTION_STATUSES),
  quoteId: z.uuid(),
  displayNumber: z.string(),
  openedAt: z.iso.datetime(),
});

export type ReceptionListItem = z.infer<typeof ReceptionListItemSchema>;

export const ListReceptionsResponseSchema = z.object({
  items: z.array(ReceptionListItemSchema),
  pagination: z.object({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
    limit: z.number().int().positive(),
  }),
});

export type ListReceptionsResponse = z.infer<typeof ListReceptionsResponseSchema>;

export const GetReceptionResponseSchema = z.object({
  id: z.uuid(),
  placeId: z.uuid(),
  partyId: z.uuid(),
  customerRoleId: z.uuid(),
  targetType: z.enum(['VEHICLE', 'PART', 'CONTAINER']),
  customerVehicleId: z.uuid().nullable(),
  containerLabel: z.string().nullable(),
  receptionMode: z.enum(['FULL', 'EXPRESS']),
  status: z.enum(RECEPTION_STATUSES),
  entryOdometerKm: z.number().int().nullable(),
  reportedIssue: z.string().nullable(),
  notes: z.string().nullable(),
  checklistSnapshot: z.array(ReceptionChecklistItemSchema).nullable(),
  belongingsSnapshot: ReceptionBelongingsSnapshotSchema.nullable(),
  termSnapshot: ReceptionTermSnapshotSchema.nullable(),
  damages: z.array(ReceptionDamageSchema),
  fuelLevelEighths: z.number().int().min(0).max(8).nullable(),
  estimatedDeliveryAt: z.iso.datetime().nullable(),
  hasReceptionTermSignature: z.boolean(),
  /** True when the reception party has an `active` consumer app account (BR-SO-151). */
  hasActiveConsumerApp: z.boolean(),
  awaitingCustomerApprovalAt: z.iso.datetime().nullable(),
  customerDecision: z.enum(['APPROVED', 'REJECTED']).nullable(),
  /** True when the titular decided in the consumer app. Portal link approval stays false. */
  customerDecidedInApp: z.boolean().default(false),
  receptionTermSignature: z
    .object({
      imageUrl: z.string().min(1),
      signedByName: z.string(),
    })
    .nullable()
    .default(null),
  quoteId: z.uuid(),
  displayNumber: z.string(),
  openedAt: z.iso.datetime(),
  /** Staff who opened the check-in; null on legacy rows (BR-SO-152). */
  openedByName: z.string().min(1).nullable(),
});

export type GetReceptionResponse = z.infer<typeof GetReceptionResponseSchema>;
